import { Suspense, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, Lightformer, ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import { XR, useXR } from "@react-three/xr";
import RoomEnvironment from "./RoomEnvironment";
import Pyramid from "./Pyramid";
import NPCs from "./NPCs";
import HeroNPCs from "./HeroNPCs";
import PublisherVault from "./PublisherVault";
import XRControls from "./XRControls";
import { tourStore } from "./tour";

// The DOM HUD is invisible inside an immersive session, so we surface a
// spatial control panel only while an XR session is active.
function InSessionControls() {
  const session = useXR((s) => s.session);
  return session ? <XRControls /> : null;
}

// Cinematic camera director for the guided tour — lerps the camera + orbit
// target toward each narrated step, and hands control back to the user after.
function Director() {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);
  const tgt = useRef(new THREE.Vector3(0, 2.2, 0));
  const goalPos = useRef(new THREE.Vector3());
  const goalTgt = useRef(new THREE.Vector3());
  const wasActive = useRef(false);

  useFrame(() => {
    const t = tourStore.getState();
    if (controls) {
      controls.autoRotate = !t.active;
      controls.enabled = !t.active;
    }
    if (t.active) {
      goalPos.current.set(...t.cam.pos);
      goalTgt.current.set(...t.cam.target);
      camera.position.lerp(goalPos.current, 0.045);
      tgt.current.lerp(goalTgt.current, 0.045);
      camera.lookAt(tgt.current);
      if (controls) controls.target.copy(tgt.current);
      wasActive.current = true;
    } else if (wasActive.current) {
      // hand orbit control back cleanly from wherever the tour ended
      if (controls) {
        controls.target.copy(tgt.current);
        controls.update();
      }
      wasActive.current = false;
    }
  });
  return null;
}

export default function Scene({ xrStore }) {
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={{ position: [8, 5, 10], fov: 42 }}
      gl={{ antialias: true, powerPreference: "high-performance" }}
    >
      <fog attach="fog" args={["#f2f1ee", 18, 44]} />
      <XR store={xrStore}>
        <Suspense fallback={null}>
          <RoomEnvironment />
          <Pyramid />
          <NPCs />
          <HeroNPCs />
          <PublisherVault />
          <Director />
          <Suspense fallback={null}>
            <InSessionControls />
          </Suspense>

          {/* procedural (no network) studio env map for glass reflections */}
          <Environment resolution={256} background={false}>
            <Lightformer intensity={3} position={[0, 6, -6]} scale={[12, 12, 1]} color="#ffffff" />
            <Lightformer intensity={2} position={[-6, 3, 4]} scale={[6, 10, 1]} color="#eaf2ff" />
            <Lightformer intensity={2} position={[6, 3, 4]} scale={[6, 10, 1]} color="#fff2ea" />
            <Lightformer intensity={1.5} form="ring" position={[0, 8, 0]} scale={4} color="#ffffff" />
          </Environment>

          <ContactShadows position={[0, 0.02, 0]} opacity={0.45} scale={18} blur={2.6} far={8} resolution={512} />

          <OrbitControls
            enablePan={false}
            minDistance={5}
            maxDistance={20}
            maxPolarAngle={Math.PI / 2.05}
            target={[0, 2.2, 0]}
            autoRotate
            autoRotateSpeed={0.35}
            makeDefault
          />
        </Suspense>
      </XR>
    </Canvas>
  );
}
